import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Button, ErrorBanner, Spinner } from '../components/ui.js';
import { formatDateTime } from '../lib/format.js';
import { errorMessage, trpcClient, type RouterOutputs } from '../lib/trpc.js';
import { AuthFrame } from './login.js';

type Opening = RouterOutputs['approvals']['openLink'];

/**
 * Answering a question an automation rule asked, from the Approve or Deny button in its email,
 * with no sign-in. The link carries its token after `#`, so it never reaches a server log, and
 * nothing is recorded until a button here is pressed: mail scanners open links on their own.
 * The email's button only says which answer to offer first.
 */
export function ApproveScreen() {
  const token = window.location.hash.replace(/^#/, '');
  const offered = new URLSearchParams(window.location.search).get('answer');
  const open = useMutation({
    mutationFn: () => trpcClient.approvals.openLink.mutate({ token }),
  });
  const answer = useMutation({
    mutationFn: (choice: 'approve' | 'deny') =>
      trpcClient.approvals.answerLink.mutate({ token, answer: choice }),
  });
  const { mutate } = open;
  useEffect(() => {
    if (token) mutate();
  }, [token, mutate]);

  if (!token)
    return (
      <AuthFrame title="This link is incomplete">
        <p className="text-sm text-slate-600">Open the link from the email again.</p>
      </AuthFrame>
    );
  const shown: Opening | undefined = answer.data ?? open.data;
  if (!shown)
    return (
      <AuthFrame title="Approval request">
        {open.error ? (
          <ErrorBanner>{errorMessage(open.error)}</ErrorBanner>
        ) : (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}
      </AuthFrame>
    );
  if (shown.state === 'invalid')
    return (
      <AuthFrame title="This link doesn’t work">
        <p className="text-sm text-slate-600" data-testid="approval-result">
          It may be incomplete, or you may no longer have an account here. Sign in to see your
          notifications.
        </p>
      </AuthFrame>
    );
  if (shown.state === 'answered')
    return (
      <AuthFrame title={outcomeTitle(shown.outcome)}>
        <p className="mb-2 text-sm font-medium text-slate-800">{shown.question}</p>
        <p className="text-sm text-slate-600" data-testid="approval-result">
          {shown.outcome === 'expired'
            ? 'Nobody answered in time, so the rule’s default decided it.'
            : shown.by_you
              ? `Thanks, your answer is recorded${shown.answered_at ? ` (${formatDateTime(shown.answered_at)})` : ''}.`
              : 'Someone else answered it first.'}
        </p>
      </AuthFrame>
    );
  // The button pressed in the email is the one shown filled in.
  const approveFirst = offered !== 'deny';
  return (
    <AuthFrame title="Approval request">
      {answer.error && <ErrorBanner>{errorMessage(answer.error)}</ErrorBanner>}
      <p className="mb-1 text-base font-semibold text-slate-900" data-testid="approval-question">
        {shown.question}
      </p>
      <p className="mb-4 text-sm text-slate-600">
        {shown.about}, {shown.company}. Answer by {formatDateTime(shown.due_at, shown.timezone)};
        with no answer by then it is {shown.if_no_answer === 'approve' ? 'approved' : 'denied'}.
      </p>
      <div className="flex gap-2">
        <Button
          variant={approveFirst ? 'primary' : 'secondary'}
          className="flex-1"
          busy={answer.isPending && answer.variables === 'approve'}
          disabled={answer.isPending}
          onClick={() => answer.mutate('approve')}
        >
          Approve
        </Button>
        <Button
          variant={approveFirst ? 'secondary' : 'danger'}
          className="flex-1"
          busy={answer.isPending && answer.variables === 'deny'}
          disabled={answer.isPending}
          onClick={() => answer.mutate('deny')}
        >
          Deny
        </Button>
      </div>
    </AuthFrame>
  );
}

function outcomeTitle(outcome: 'approved' | 'denied' | 'expired'): string {
  if (outcome === 'approved') return 'Approved';
  if (outcome === 'denied') return 'Denied';
  return 'No answer in time';
}
